// Mock data for College Journey Showcase
// Features Sheppard - a civil engineering aspirant

export const sheppardProfile = {
  // Basic Profile
  profile: {
    name: "Sheppard",
    grade: 12,
    school: "Oak Ridge High School",
    gpa: 3.92,
    weightedGPA: 4.45,
    satScore: 1540,
    actScore: 35,
    email: "sheppard.demo@example.com",
    interests: ["Civil Engineering", "Urban Planning", "Sustainable Infrastructure"],
    careerGoals: ["Infrastructure Innovation Engineer", "Sustainable City Designer"],

    // Activities for brag sheet
    activities: [
      {
        name: "Engineering Club President",
        years: "10-12",
        hoursPerWeek: 12,
        description: "Designed pedestrian bridge proposal for city council, led team of 15"
      },
      {
        name: "Habitat for Humanity Youth Leader",
        years: "9-12",
        hoursPerWeek: 10,
        description: "Coordinated builds, trained 30+ volunteers in construction safety"
      },
      {
        name: "Math Team Captain",
        years: "11-12",
        hoursPerWeek: 8,
        description: "Led team to state finals, specialized in applied mathematics problems"
      },
      {
        name: "City Planning Commission - Student Representative",
        years: "11-12",
        hoursPerWeek: 5,
        description: "Youngest member ever appointed, reviewed infrastructure proposals"
      }
    ],

    awards: [
      "ASCE National Bridge Building Champion",
      "AP Scholar with Distinction",
      "State Mathematics Competition - 1st Place",
      "Mayor's Youth Leadership Award"
    ]
  },

  // Strategic Assessment Results
  assessment: {
    completed: true,
    insights: {
      uniqueStrength: "You see infrastructure not just as engineering problems, but as human solutions",
      hiddenGem: "Your work with Habitat for Humanity shows hands-on experience most engineering applicants lack",
      coreValue: "Building structures that serve communities, not just function",
      differentiator: "You're already operating at a professional level - city planning commission work is exceptionally rare"
    },

    threadAnalysis: {
      primary: "Builder of Communities",
      secondary: "Systems Thinker",
      tertiary: "Practical Visionary"
    },

    recommendations: [
      "The city planning commission appointment is your crown jewel - this shows exceptional maturity",
      "Connect your bridge building championship to real-world impact through Habitat work",
      "Your mathematical excellence supports engineering credibility - don't minimize it"
    ]
  },

  // College List with Strategy
  colleges: [
    {
      id: 1,
      name: "MIT",
      tier: "reach",
      fitScore: 94,
      deadline: "2025-01-01",
      status: "researching",
      whyFit: "World's best engineering program, urban planning minor, UROP research opportunities",
      strategy: "Emphasize hands-on building experience and systems thinking approach",
      essays: {
        main: "Describe the world you come from (250 words)",
        supplemental: ["What you do for fun (200 words)", "Collaborative work (250 words)"]
      },
      requirements: {
        recommendations: 2,
        satRequired: true,
        interviews: "Required"
      }
    },
    {
      id: 2,
      name: "Caltech",
      tier: "reach",
      fitScore: 91,
      deadline: "2025-01-03",
      status: "researching",
      whyFit: "Small community, intense engineering focus, Earthquake Engineering Research Lab",
      strategy: "Highlight mathematical excellence and research interests",
      essays: {
        main: "STEM experience (300 words)",
        supplemental: ["Diversity of perspective (200 words)", "Fun fact (50 words)"]
      }
    },
    {
      id: 3,
      name: "UC Berkeley",
      tier: "target",
      fitScore: 89,
      deadline: "2024-11-30",
      status: "drafting",
      whyFit: "Top civil engineering program, proximity to urban planning challenges",
      strategy: "Leverage California residence and city planning experience",
      essays: {
        main: "Personal Insight Questions (4 required)",
        supplemental: []
      }
    },
    {
      id: 4,
      name: "Georgia Tech",
      tier: "target",
      fitScore: 87,
      deadline: "2025-01-04",
      status: "researching",
      whyFit: "Strong civil engineering, co-op program, urban systems focus",
      strategy: "Emphasize practical experience and interest in infrastructure innovation"
    },
    {
      id: 5,
      name: "Purdue",
      tier: "safety",
      fitScore: 85,
      deadline: "2025-01-15",
      status: "ready",
      whyFit: "Excellent engineering reputation, strong alumni network, good merit aid",
      strategy: "Apply early action for honors college consideration"
    }
  ],

  // Timeline Tasks (before and after batching)
  tasks: {
    raw: [
      { id: 1, title: "Write Brown supplement", hours: 3, type: "essay", college: "Brown" },
      { id: 2, title: "Write UC Berkeley PIQ 1", hours: 2, type: "essay", college: "UC Berkeley" },
      { id: 3, title: "Write UC Berkeley PIQ 2", hours: 2, type: "essay", college: "UC Berkeley" },
      { id: 4, title: "Request transcript for Brown", hours: 0.5, type: "admin", college: "Brown" },
      { id: 5, title: "Request transcript for Berkeley", hours: 0.5, type: "admin", college: "UC Berkeley" },
      { id: 6, title: "Complete Brown activities section", hours: 1, type: "application", college: "Brown" },
      { id: 7, title: "Complete Berkeley activities section", hours: 1, type: "application", college: "UC Berkeley" },
      { id: 8, title: "Research Brown professors", hours: 2, type: "research", college: "Brown" },
      { id: 9, title: "Research Berkeley programs", hours: 2, type: "research", college: "UC Berkeley" },
      { id: 10, title: "Draft recommendation request", hours: 1, type: "recommendations" },
      { id: 11, title: "Update resume", hours: 1.5, type: "profile" },
      { id: 12, title: "Complete FAFSA", hours: 2, type: "financial" }
    ],

    batched: [
      {
        id: "batch-1",
        title: "Essay Writing Sprint - UC System",
        type: "focus_session",
        tasks: [2, 3],
        totalHours: 4,
        optimizedHours: 3,
        date: "2024-10-15",
        insight: "Write both PIQs together - similar themes"
      },
      {
        id: "batch-2",
        title: "Quick Admin Tasks",
        type: "quick_wins",
        tasks: [4, 5],
        totalHours: 1,
        optimizedHours: 0.5,
        date: "2024-10-16",
        insight: "Request all transcripts at once"
      },
      {
        id: "batch-3",
        title: "Research Deep Dive",
        type: "research_session",
        tasks: [8, 9],
        totalHours: 4,
        optimizedHours: 3,
        date: "2024-10-17",
        insight: "Research schools back-to-back while in analytical mindset"
      }
    ]
  },

  // Essay Development
  essays: {
    commonApp: {
      prompt: "Describe a problem you've solved or would like to solve.",
      status: "revision",
      versions: [
        {
          version: 1,
          excerpt: "The problem of electronic waste has always bothered me...",
          feedback: "Good topic but too generic opening"
        },
        {
          version: 2,
          excerpt: "At 3 AM, surrounded by circuit boards and coffee cups, I finally understood...",
          feedback: "Much stronger! Shows personality immediately"
        }
      ],
      currentDraft: `At 3 AM, surrounded by circuit boards and coffee cups, I finally understood why
      our local farm was struggling. It wasn't just drought – it was inefficiency. Six months later,
      my solar-powered irrigation robot was reducing their water usage by 30% while increasing yield...`
    },

    supplementals: {
      brown: {
        status: "drafting",
        prompt: "Why Brown?",
        outline: [
          "Open curriculum connection to interdisciplinary goals",
          "Specific professors in CS + Environmental Science",
          "Providence sustainability initiatives"
        ]
      }
    }
  },

  // Recommendation System
  recommendations: {
    requests: [
      {
        recommender: "Ms. Rodriguez",
        subject: "AP Computer Science",
        relationship: "Taught Sheppard for 2 years, Engineering Club advisor",
        status: "requested",
        matchScore: 95,
        talkingPoints: [
          "Solar-powered robot project",
          "Mentored younger students",
          "Creative problem-solving approach"
        ],
        generatedEmail: `Dear Ms. Rodriguez,

I hope you're doing well! I'm writing to ask if you would be willing to write a letter of recommendation
for my college applications. Your AP Computer Science class and guidance in Robotics have been instrumental
in shaping my passion for using technology to solve environmental problems.

I'm applying to schools like Brown and UC Berkeley, where I hope to combine Computer Science with
Environmental Science. I think you could speak uniquely to my technical skills and how I applied them
to real-world problems, especially our solar-powered robot project that's now helping local farms...`
      }
    ]
  },

  // Smart Insights
  insights: [
    {
      id: 1,
      type: "strategic",
      priority: "high",
      title: "Strategic prerequisite",
      message: "Complete your brag sheet first - it will auto-populate 5 application sections",
      action: "Start brag sheet",
      impact: "Save 3 hours of redundant work"
    },
    {
      id: 2,
      type: "deadline",
      priority: "urgent",
      title: "UC deadline approaching",
      message: "UC applications close in 15 days - you're 70% complete",
      action: "Finish UC essays",
      impact: "4 schools with one application"
    },
    {
      id: 3,
      type: "opportunity",
      priority: "medium",
      title: "Perfect batch opportunity",
      message: "You have 6 quick tasks that could be done in one 45-minute session",
      action: "Start quick wins session",
      impact: "Clear your plate for deep work tomorrow"
    }
  ],

  // Success Metrics
  metrics: {
    before: {
      totalTasks: 47,
      overdueeTasks: 5,
      stressLevel: 8,
      hoursEstimated: 89,
      completionRate: "12%"
    },
    after: {
      totalBatches: 14,
      tasksCompleted: 47,
      stressLevel: 4,
      hoursSaved: 30,
      completionRate: "100%",
      deadlinesMet: "100%"
    }
  },

  // Brag Sheet Data
  bragSheet: {
    academicHighlights: [
      "4.2 Weighted GPA - Top 10% of class",
      "1480 SAT (780 Math, 700 Reading)",
      "5 APs: Computer Science A (5), Environmental Science (5), Calculus BC (5)"
    ],
    leadershipExamples: [
      "Robotics Team Captain - Led 12-person team to state championships",
      "Environmental Club Founder - Grew from 5 to 45 members in one year",
      "Peer Tutor - Helped 15 students improve grades in math/science"
    ],
    uniqueQualities: [
      "Bridges technical skills with environmental activism",
      "Teaches coding to elderly community members every Saturday",
      "Speaks three languages: English, Mandarin, Spanish"
    ],
    challengesOvercome: [
      "Learned to code entirely self-taught before taking any CS classes",
      "Balanced heavy course load with 20+ hours of activities weekly",
      "Overcame initial shyness to become effective team leader"
    ]
  }
};

// Timeline progression animation data
export const timelineProgression = [
  { day: "Monday", tasks: 8, stress: "high", color: "red" },
  { day: "Tuesday", tasks: 6, stress: "high", color: "orange" },
  { day: "Wednesday", tasks: 5, stress: "medium", color: "yellow" },
  { day: "Thursday", tasks: 3, stress: "low", color: "emerald" },
  { day: "Friday", tasks: 2, stress: "low", color: "emerald" }
];

// Sample conversations for assessment demo
export const assessmentConversation = [
  {
    type: "bot",
    message: "Tell me about something you know a lot about that most people don't."
  },
  {
    type: "user",
    message: "I know way too much about irrigation systems because I spent last summer helping my neighbor's farm survive the drought. I ended up building them a solar-powered robot that monitors soil moisture."
  },
  {
    type: "bot",
    message: "That's fascinating! You combined environmental awareness with technical innovation. Tell me more about a time you stood up for something even when it was difficult."
  }
];

// Animation timings for smooth showcase
export const animationTimings = {
  fadeIn: 600,
  slideIn: 800,
  batchAnimation: 1200,
  typewriter: 50, // ms per character
  progressBar: 2000,
  countUp: 1500
};

// Default export
export default sheppardProfile;